export const materialKeys = {
  all: ["materials"] as const,
  // Per-type item-count contract (min/max/default) — rarely changes,
  // shared across every RequestMaterialModal instance.
  types: () => [...materialKeys.all, "types"] as const,
  lists: () => [...materialKeys.all, "list"] as const,
  // PRD-05 §FR-6 (2026-08-05) — one cache entry per classroom scope holding
  // every material in it. Filtering, sorting and paging all happen in memory
  // downstream, so the key deliberately carries NO filter params: changing a
  // filter must not evict the fetch.
  scope: (classroomId: string | null) => [...materialKeys.lists(), classroomId ?? "all"] as const,
  // PRD-62 — the "Archivados" section: its count (fetched with the page) and
  // its rows (fetched only once opened). Under `lists()` so every invalidation
  // of the lanes refreshes them too.
  archived: (classroomId: string | null) =>
    [...materialKeys.lists(), "archived", classroomId ?? "all"] as const,
  archivedCount: (classroomId: string | null) =>
    [...materialKeys.archived(classroomId), "count"] as const,
  details: () => [...materialKeys.all, "detail"] as const,
  detail: (id: string) => [...materialKeys.details(), id] as const,
  // PRD-11 — real-time progress for the per-student strip.
  progress: (id: string) => [...materialKeys.detail(id), "progress"] as const,
  // PRD-61 §FR-5.1 — the evaluation cut this material counts for.
  cut: (id: string) => [...materialKeys.detail(id), "cut"] as const,
  // Per-student MaterialAssignment list keyed by Material id.
  assignmentsForMaterial: (id: string) => [...materialKeys.detail(id), "assignments"] as const,
  // One student's release, keyed by ASSIGNMENT id. Under `all` so every
  // material-wide invalidation (approve, regenerate) refreshes it too.
  assignment: (assignmentId: string) => [...materialKeys.all, "assignment", assignmentId] as const,
  // The short-lived signed URL the preview drawer points its iframe at.
  // It lived inline as ["assignment-pdf-url", id, view] until PRD-65.
  assignmentPdfUrl: (assignmentId: string, view: "student" | "professor") =>
    [...materialKeys.assignment(assignmentId), "pdf-url", view] as const,
  // PRD-52 §FR-9 — the `codigo` starter file, immutable per release.
  assignmentStarterCode: (assignmentId: string, releaseKey: string | null) =>
    [...materialKeys.assignment(assignmentId), "starter-code", releaseKey] as const,
  // PRD-65 — the editable content + draft + edit state.
  assignmentContent: (assignmentId: string) =>
    [...materialKeys.assignment(assignmentId), "content"] as const,
  // The gradeable-materials picker in GradeEntryModal. It used to build
  // ["materials", "for-grading", classroomId] inline, which sits under
  // `all` — so the four call sites that invalidate `materialKeys.all`
  // silently evicted it (audit F-FE-9). Declared here so the collision is
  // visible: it IS material data and SHOULD be refreshed when materials
  // change, which the inline key achieved only by accident.
  forGrading: (classroomId: string) => [...materialKeys.all, "for-grading", classroomId] as const,
}

export const studentMaterialsKeys = {
  all: ["student-materials"] as const,
  // Prefix for "every cached list for this student" — matches all the
  // (page, pageSize) permutations a tab might have open. Used by the
  // realtime invalidator (PRD-18 §FR-3.4) to refresh the "Materiales
  // personalizados" section when an assignment flips to READY.
  byStudent: (studentId: string) => [...studentMaterialsKeys.all, studentId] as const,
  list: (studentId: string, page: number, pageSize: number) =>
    [...studentMaterialsKeys.byStudent(studentId), page, pageSize] as const,
}
